// SyncBadge — trust-attribution label for auto-synced data.
//
// Placeholder slot for Phase A of the stats pipeline (see
// STATS_PIPELINE_NOTES.md). When `source` + `syncedAt` are both non-null,
// renders "Auto-synced from {source} · X days ago" with a small icon. Both
// null today (nothing is auto-synced yet) → renders nothing. Wired into
// PlayerHero now so Phase A can flip a switch.

import { RefreshCw } from 'lucide-react'

interface Props {
  source: string | null
  syncedAt: string | null // ISO-8601
}

// Coach-facing label map — raw DB source values are internal identifiers
// and must not leak into the UI ("api_basketball" → "official league data",
// per 07-15 Cang-package fix pass). Unknown sources fall back to the raw
// value so a new pipeline source is never rendered as blank.
const SOURCE_LABELS: Record<string, string> = {
  api_basketball: 'official league data',
}

function ago(iso: string): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return iso
  const diff = Date.now() - then
  const days = Math.floor(diff / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day ago'
  if (days < 30) return `${days} days ago`
  const months = Math.floor(days / 30)
  if (months === 1) return '1 month ago'
  return `${months} months ago`
}

export default function SyncBadge({ source, syncedAt }: Props) {
  if (!source || !syncedAt) return null
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-hoop-text-3">
      <RefreshCw className="w-3 h-3" />
      Auto-synced from {SOURCE_LABELS[source] ?? source} · {ago(syncedAt)}
    </span>
  )
}
